// Modul pro správu položek nabídky (složené kalkulace)
import Sortable from 'sortablejs';
import { icon } from './icons.js';

export class OfferItemsManager {
  constructor() {
    this.items = [];
    this.activeItemId = null;
    this.itemIdCounter = 0;
    this.sortable = null;
    this.selectedCustomer = null;
    this._bulkRecalculating = false;
    // Původ typu ceny: 'customer' = odvozeno od vybraného odběratele,
    // 'manual' = uživatel přepnul ručně (override).
    this.typCenySource = 'manual';
    this.init();
  }

  init() {
    // Vytvoření první položky s aktuálním stavem formuláře
    const currentFormState = this.saveCurrentFormState();
    
    const firstItem = {
      id: ++this.itemIdCounter,
      name: this.generateItemName(),
      formData: currentFormState,
      price: 0,
      pricePerPiece: 0
    };

    this.items.push(firstItem);
    this.activeItemId = firstItem.id;
    
    // Event listenery
    this.setupEventListeners();
    
    // Vykreslit položky
    this.renderItems();
    
    // Aktualizovat celkovou cenu
    this.updateTotalPrice();
  }

  setupEventListeners() {
    // Tlačítko pro přidání položky
    const addButton = document.getElementById('pridatPolozku');
    if (addButton) {
      addButton.addEventListener('click', () => this.addItem());
    }

    // Tlačítko pro vytvoření nabídky
    const createOfferButton = document.getElementById('vytvorNabidku');
    if (createOfferButton) {
      createOfferButton.addEventListener('click', () => this.createOffer());
    }

    // Změna typu ceny (maloobchodní/velkoobchodní) – globální pro celou nabídku:
    // přepočítáme všechny položky a sečteme je. stopPropagation zabrání tomu,
    // aby se spustil i běžný change-listener na formuláři #ceny (ten by přepočítal
    // jen aktivní položku). Pozor: restoreFormState nastavuje typCeny programově
    // (při _applyingPreset) – tehdy listener ignorujeme, jinak by došlo k rekurzi.
    // Vizuální stav segmentovaného toggle se ale synchronizuje vždy.
    const typCenySelect = document.getElementById('typCeny');
    if (typCenySelect) {
      typCenySelect.addEventListener('change', (e) => {
        this.syncTypCenyToggle();
        if (window._applyingPreset || this._bulkRecalculating) return;
        e.stopPropagation();
        this.recalculateAllItems(e.target.value);
      });
    }

    // Segmentovaný toggle – vizuální řadič nad skrytým selectem #typCeny.
    const typCenyToggle = document.getElementById('typCenyToggle');
    if (typCenyToggle) {
      typCenyToggle.querySelectorAll('.typ-ceny-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (window._applyingPreset || this._bulkRecalculating) return;
          const value = btn.dataset.typCeny;
          if (!value) return;
          if (typCenySelect && typCenySelect.value !== value) {
            this.typCenySource = 'manual';
            typCenySelect.value = value;
            typCenySelect.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            this.syncTypCenyToggle();
          }
        });
      });
    }

    // Iniciální vizuální stav toggle
    this.syncTypCenyToggle();

    // Vyhledávání odběratele v tabu "Dodání a fakturace"
    this.setupCustomerSearch();
  }

  // ----- Vyhledávač odběratele -----

  setupCustomerSearch() {
    const input = document.getElementById('vyhledatOdberatele');
    const resultsBox = document.getElementById('vyhledatOdberateleResults');
    if (!input || !resultsBox) return;

    let debounceTimer = null;
    let lastQuery = '';
    let activeRequestToken = 0;

    const hideResults = () => {
      resultsBox.classList.add('hidden');
      resultsBox.innerHTML = '';
    };

    const showResults = (html) => {
      resultsBox.innerHTML = html;
      resultsBox.classList.remove('hidden');
    };

    const runSearch = async (query) => {
      const trimmed = query.trim();
      if (trimmed.length < 2) {
        hideResults();
        return;
      }
      if (trimmed === lastQuery) return;
      lastQuery = trimmed;

      if (!window.customersManager) {
        showResults(`<div class="px-4 py-3 text-sm text-gray-500">Správa zákazníků není dostupná.</div>`);
        return;
      }

      const token = ++activeRequestToken;
      try {
        const customers = await window.customersManager.fetchCustomers(trimmed);
        if (token !== activeRequestToken) return;

        if (!customers.length) {
          showResults(`<div class="px-4 py-3 text-sm text-gray-500">Žádný odběratel neodpovídá „${this.escapeHtml(trimmed)}".</div>`);
          return;
        }

        const html = customers.slice(0, 10).map((c, i) => `
          <button type="button" data-customer-idx="${i}"
            class="w-full text-left px-4 py-2.5 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors">
            <div class="flex items-center justify-between gap-3">
              <div class="min-w-0 flex-1">
                <div class="text-sm font-medium text-gray-900 truncate">${this.escapeHtml(c.name || '(bez názvu)')}</div>
                <div class="mt-0.5 text-xs text-gray-500 truncate">
                  ${[c.ico ? `IČO ${this.escapeHtml(c.ico)}` : '', c.city ? this.escapeHtml(c.city) : '', c.email ? this.escapeHtml(c.email) : '']
                    .filter(Boolean).join(' · ')}
                </div>
              </div>
              <span class="shrink-0 text-[11px] px-2 py-0.5 rounded-full ${c.type === 'person' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}">
                ${c.type === 'person' ? 'Osoba' : 'Firma'}
              </span>
            </div>
          </button>
        `).join('');

        showResults(html);

        resultsBox.querySelectorAll('[data-customer-idx]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const idx = Number(btn.dataset.customerIdx);
            const customer = customers[idx];
            if (customer) this.selectCustomer(customer);
            hideResults();
          });
        });
      } catch (err) {
        if (token !== activeRequestToken) return;
        const msg = err?.message || 'Chyba při vyhledávání zákazníka';
        showResults(`<div class="px-4 py-3 text-sm text-red-600">${this.escapeHtml(msg)}</div>`);
      }
    };

    input.addEventListener('input', () => {
      const value = input.value;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => runSearch(value), 250);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') hideResults();
    });

    // Klik mimo dropdown ho zavře
    document.addEventListener('click', (e) => {
      if (!resultsBox.contains(e.target) && e.target !== input) {
        hideResults();
      }
    });
  }

  // Synchronizace vizuálního stavu segmentovaného toggle Typ ceny se skrytým
  // selectem #typCeny. Zároveň zobrazí hint, pokud je typ ceny odvozen od
  // vybraného zákazníka.
  syncTypCenyToggle() {
    const select = document.getElementById('typCeny');
    const toggle = document.getElementById('typCenyToggle');
    const hint = document.getElementById('typCenyHint');
    if (!select || !toggle) return;
    const value = select.value || 'velko';
    toggle.querySelectorAll('.typ-ceny-btn').forEach((btn) => {
      const active = btn.dataset.typCeny === value;
      btn.classList.toggle('bg-green-600', active);
      btn.classList.toggle('text-white', active);
      btn.classList.toggle('text-gray-600', !active);
      btn.classList.toggle('hover:text-gray-900', !active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (hint) {
      hint.textContent = this.typCenySource === 'customer' ? 'odvozeno od zákazníka' : '';
    }
  }

  selectCustomer(customer) {
    if (!customer) return;
    this.selectedCustomer = customer;

    // Vyplnit fakturační pole v aktivní položce
    const fakturaceEl = document.getElementById('fakturace');
    if (fakturaceEl && window.CustomersManager) {
      fakturaceEl.value = window.CustomersManager.formatCustomerToText(customer);
    }
    const fakturaMailEl = document.getElementById('fakturaMail');
    if (fakturaMailEl) fakturaMailEl.value = customer.email || '';
    const telefonEl = document.getElementById('telefonDoprava');
    if (telefonEl) telefonEl.value = customer.phone || '';

    // Odvodit typ ceny z cenové úrovně zákazníka (velko/malo) a přepočítat
    // celou nabídku. Source of truth je skrytý select #typCeny.
    const tier = customer.pricingTier === 'malo' ? 'malo' : 'velko';
    const typCenySelect = document.getElementById('typCeny');
    if (typCenySelect) {
      this.typCenySource = 'customer';
      if (typCenySelect.value !== tier) {
        typCenySelect.value = tier;
        typCenySelect.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.syncTypCenyToggle();
      }
    }

    // Reset vyhledávacího inputu a zobrazit chip
    const input = document.getElementById('vyhledatOdberatele');
    if (input) input.value = '';
    this.renderSelectedCustomer();

    // Uložit do aktivní položky, aby se při přepnutí/uložení zachovalo
    if (this.activeItemId) {
      const current = this.items.find((i) => i.id === this.activeItemId);
      if (current) current.formData = this.saveCurrentFormState();
    }
  }

  clearSelectedCustomer() {
    this.selectedCustomer = null;
    // Bez zákazníka už typ ceny není odvozen – přepnutí zpět na manuální.
    this.typCenySource = 'manual';
    this.syncTypCenyToggle();
    this.renderSelectedCustomer();
  }

  renderSelectedCustomer() {
    const box = document.getElementById('vybranyOdberatel');
    if (!box) return;

    if (!this.selectedCustomer) {
      box.classList.add('hidden');
      box.innerHTML = '';
      return;
    }

    const c = this.selectedCustomer;
    const meta = [
      c.ico ? `IČO ${this.escapeHtml(c.ico)}` : '',
      c.dic ? `DIČ ${this.escapeHtml(c.dic)}` : '',
      c.city ? this.escapeHtml(c.city) : ''
    ].filter(Boolean).join(' · ');

    box.classList.remove('hidden');
    box.innerHTML = `
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-sm font-semibold text-gray-900 truncate">${this.escapeHtml(c.name || '')}</span>
            <span class="text-[11px] px-2 py-0.5 rounded-full ${c.type === 'person' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}">
              ${c.type === 'person' ? 'Osoba' : 'Firma'}
            </span>
            <span class="text-[11px] px-2 py-0.5 rounded-full ${c.pricingTier === 'malo' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}" title="Cenová úroveň zákazníka">
              ${c.pricingTier === 'malo' ? 'Maloobchod' : 'Velkoobchod'}
            </span>
          </div>
          ${meta ? `<div class="mt-0.5 text-xs text-gray-600">${meta}</div>` : ''}
          ${(c.email || c.phone) ? `<div class="mt-0.5 text-xs text-gray-500">${[c.email ? this.escapeHtml(c.email) : '', c.phone ? this.escapeHtml(c.phone) : ''].filter(Boolean).join(' · ')}</div>` : ''}
        </div>
        <button type="button" id="odebratOdberatele"
          class="shrink-0 p-1 text-gray-400 hover:text-red-600 focus:outline-none"
          title="Odebrat vybraného odběratele">
          ${icon('x-mark', 'w-4 h-4')}
        </button>
      </div>
    `;

    document.getElementById('odebratOdberatele')?.addEventListener('click', () => this.clearSelectedCustomer());
  }

  // Přidání nové položky s výchozími hodnotami
  addItem() {
    const itemId = ++this.itemIdCounter;
    const currentFormState = this.saveCurrentFormState();
    
    // Uložit stav aktuální položky
    if (this.activeItemId) {
      const currentItem = this.items.find(item => item.id === this.activeItemId);
      if (currentItem) {
        currentItem.formData = currentFormState;
      }
    }

    // Vytvořit novou položku s výchozími hodnotami
    const defaultFormData = this.getDefaultFormData();
    const newItem = {
      id: itemId,
      name: this.generateDefaultItemName(),
      formData: defaultFormData,
      price: 0,
      pricePerPiece: 0
    };

    this.items.push(newItem);
    this.activeItemId = itemId;
    
    // Načíst výchozí hodnoty do formuláře
    this.restoreFormState(defaultFormData);
    this.renderItems();
  }

  // Duplikování aktuální položky
  duplicateItem(itemId) {
    const sourcItem = this.items.find(item => item.id === itemId);
    if (!sourcItem) return;

    const newItemId = ++this.itemIdCounter;
    const currentFormState = this.saveCurrentFormState();
    
    // Uložit aktuální stav do zdrojové položky pokud je aktivní
    if (this.activeItemId === itemId) {
      sourcItem.formData = currentFormState;
    }

    // Vytvořit duplikát s kopií dat ze zdrojové položky
    const newItem = {
      id: newItemId,
      name: sourcItem.name,
      formData: { ...sourcItem.formData },
      price: 0,
      pricePerPiece: 0
    };

    this.items.push(newItem);
    this.activeItemId = newItemId;
    
    // Načíst data duplikátu do formuláře
    this.restoreFormState(newItem.formData);
    this.renderItems();
  }

  // Odstranění položky
  removeItem(itemId) {
    if (this.items.length <= 1) {
      return; // Nelze odstranit poslední položku
    }

    const index = this.items.findIndex(item => item.id === itemId);
    if (index > -1) {
      this.items.splice(index, 1);
      
      // Pokud byla odstraněna aktivní položka, aktivovat jinou
      if (this.activeItemId === itemId) {
        this.activeItemId = this.items[0].id;
        this.switchToItem(this.activeItemId);
      }
      
      this.renderItems();
      this.updateTotalPrice();
    }
  }

  // Přepnutí na jinou položku
  switchToItem(itemId) {
    if (this.activeItemId === itemId) return;

    // Uložit aktuální stav formuláře
    if (this.activeItemId) {
      const currentItem = this.items.find(item => item.id === this.activeItemId);
      if (currentItem) {
        currentItem.formData = this.saveCurrentFormState();
      }
    }

    // Přepnout na novou položku
    const newItem = this.items.find(item => item.id === itemId);
    if (newItem) {
      this.activeItemId = itemId;
      this.restoreFormState(newItem.formData);
      this.renderItems();
    }
  }

  // Aktualizace ceny položky
  updateItemPrice(price, pricePerPiece, debugInfo, calculationResult = null) {
    const currentItem = this.items.find(item => item.id === this.activeItemId);
    if (currentItem) {
      currentItem.price = price;
      currentItem.pricePerPiece = pricePerPiece;
      currentItem.debugInfo = debugInfo;
      if (calculationResult) {
        currentItem.calculationResult = calculationResult;
      }
      
      // Aktualizovat název položky podle nového formátu
      currentItem.name = this.generateItemName();
      
      // Během hromadného přepočtu (změna typu ceny pro celou nabídku)
      // vykreslování a aktualizaci celkové ceny potlačíme – provede se
      // najednou až na konci v recalculateAllItems().
      if (this._bulkRecalculating) return;
      
      this.renderItems();
      this.updateTotalPrice();
    }
  }

  // Generování výchozího názvu položky
  generateDefaultItemName() {
    const defaultData = this.getDefaultFormData();
    
    // Základní název s výchozími hodnotami
    let nameParts = [];
    
    // Počet kusů
    nameParts.push(`${defaultData.pocetKusu}ks`);
    
    // Formát A4
    nameParts.push('A4 (210×297 mm)');
    
    // Barevnost 4/4
    nameParts.push('4/4');
    
    // Materiál (najdeme v selectu)
    const materialSelect = document.getElementById('material');
    if (materialSelect) {
      const defaultOption = materialSelect.querySelector(`option[value="${defaultData.material}"]`);
      if (defaultOption) {
        nameParts.push(defaultOption.text);
      } else {
        nameParts.push('Výchozí materiál');
      }
    }
    
    return nameParts.join(', ');
  }

  // Generování názvu položky ve formátu stejném jako "Výsledek kalkulace"
  generateItemName() {
    const nazev = document.getElementById('nazev')?.value?.trim();
    const pocet = document.getElementById('pocetKusu')?.value || '0';
    const format = document.getElementById('produktRozmerFormat');
    const pocetBarev = document.getElementById('pocetBarev');
    const material = document.getElementById('material');
    
    // Sestavit základní část názvu
    let nameParts = [];
    
    // Počet kusů
    nameParts.push(`${pocet}ks`);
    
    // Formát s rozměry
    if (format) {
      const selectedOption = format.options[format.selectedIndex];
      if (selectedOption) {
        const formatText = selectedOption.text;
        
        if (format.value === 'vlastni') {
          const vlastniSirka = document.getElementById('produktRozmerSirka')?.value || '0';
          const vlastniVyska = document.getElementById('produktRozmerVyska')?.value || '0';
          nameParts.push(`Vlastní (${vlastniSirka}×${vlastniVyska} mm)`);
        } else {
          // formatText už obsahuje rozměry z HTML (např. "A3 (297×420 mm)")
          nameParts.push(formatText);
        }
      }
    }
    
    // Barevnost s PANTONE barvami (připojené bez čárky)
    if (pocetBarev) {
      const selectedOption = pocetBarev.options[pocetBarev.selectedIndex];
      if (selectedOption) {
        let barevnostText = selectedOption.text;
        
        // Přidat PANTONE barvy pokud jsou vyplněné (připojené k barevnosti)
        const primeBarvy = document.getElementById('primeBarvy')?.value;
        if (primeBarvy && primeBarvy !== '0') {
          const pocetPrimeBarev = parseInt(primeBarvy);
          if (pocetPrimeBarev === 1) {
            barevnostText += ' + 1 PANTONE';
          } else if (pocetPrimeBarev > 1) {
            barevnostText += ` + ${pocetPrimeBarev} PANTONE`;
          }
        }
        
        nameParts.push(barevnostText);
      }
    }

    // Materiál na konec. Dokončovací operace (lakování, laminace, bigování, …)
    // se do jména NEDOSTÁVAJÍ – zobrazují se samostatně pod názvem jako menší
    // šedý výpis (viz OfferEditorManager.formatItemSpecification).
    if (material) {
      const selectedOption = material.options[material.selectedIndex];
      if (selectedOption) {
        const materialText = selectedOption.text;
        // materialText už obsahuje gramáž z databáze (např. "Ofset 80 g/m²")
        nameParts.push(materialText);
      }
    }
    
    const baseName = nameParts.join(', ');
    
    // Pokud je vyplněný název zakázky, přidat ho na začátek
    if (nazev) {
      return `${nazev}: ${baseName}`;
    } else {
      return baseName;
    }
  }

  // Hromadný přepočet všech položek nabídky při změně typu ceny
  // (maloobchodní/velkoobchodní). typCeny je globální pro celou nabídku:
  // přepíná se jedním selectem, ale přepočítají se všechny položky a z nich
  // se teprve sčítá celková cena nabídky.
  async recalculateAllItems(newTypCeny) {
    if (this._bulkRecalculating) return;
    const originalActiveId = this.activeItemId;

    // Uložit aktuální živý stav formuláře do aktivní položky. Je to nutné,
    // protože formulář se do formData aktivní položky běžně ukládá až při
    // přepnutí/přidání položky – aktivní položka by tak měla zastaralá data
    // (např. materiál změněný uživatelem) a obnova by ho přepsala výchozím.
    if (this.activeItemId) {
      const current = this.items.find((i) => i.id === this.activeItemId);
      if (current) current.formData = this.saveCurrentFormState();
    }

    // Synchronizovat typ ceny ve všech položkách (aby při pozdějším přepnutí
    // aktivní položky select ukazoval stejnou hodnotu).
    this.items.forEach((item) => {
      if (item.formData) item.formData.typCeny = newTypCeny;
    });

    this._bulkRecalculating = true;
    try {
      // Postupně načíst formulář pro každou položku, přepočítat a uložit její cenu.
      for (const item of this.items) {
        this.activeItemId = item.id;
        const promise = this.restoreFormState(item.formData);
        if (promise) await promise;
      }
    } finally {
      this._bulkRecalculating = false;
    }

    // Obnovit původně aktivní položku do formuláře a překreslit seznam.
    this.activeItemId = originalActiveId;
    const original = this.items.find((i) => i.id === originalActiveId);
    if (original) {
      this.restoreFormState(original.formData);
    }
    this.renderItems();
    this.updateTotalPrice();
  }

  // Pomocná funkce pro získání textů dodatečných služeb
  getAdditionalServicesText() {
    const dodatecneUpravy = [];

    // Digitální speciální barvy
    const barvyDigital = document.getElementById('barvyDigital')?.value;
    if (barvyDigital && barvyDigital !== '0') {
      const digitalText = this.getDigitalColorName(barvyDigital);
      if (digitalText) {
        dodatecneUpravy.push(`Digitální speciální barva: ${digitalText}`);
      }
    }

    // Lakování
    const lak = document.getElementById('lakovani')?.value;
    if (lak && lak !== '0') {
      const lakText = this.getLakText(lak);
      if (lakText) {
        dodatecneUpravy.push(`Lakování: ${lakText}`);
      }
    }

    // Laminace
    const lamino = document.getElementById('laminace')?.value;
    if (lamino && lamino !== '0') {
      const laminaceText = this.getLaminaceText(lamino);
      if (laminaceText) {
        dodatecneUpravy.push(`Laminace: ${laminaceText}`);
      }
    }

    // Falcování
    const falcovani = document.getElementById('falcovani')?.value;
    if (falcovani && falcovani !== '0') {
      const falcovaniText = this.getFalcovaniText(falcovani);
      if (falcovaniText) {
        dodatecneUpravy.push(`Falcování: ${falcovaniText}`);
      }
    }

    // Bigování
    const bigovani = document.getElementById('bigovani')?.value;
    if (bigovani && bigovani !== '0') {
      const bigovaniText = this.getBigovaniText(bigovani);
      if (bigovaniText) {
        dodatecneUpravy.push(`Bigování: ${bigovaniText}`);
      }
    }

    // Výsek
    const vysek = document.getElementById('vysek')?.value;
    if (vysek && vysek !== '0') {
      const vysekText = this.getVysekText(vysek);
      const pozicNaVysek = document.getElementById('pozicNaVysek')?.value || '1';
      if (vysekText) {
        dodatecneUpravy.push(`Výsek: ${vysekText} (${pozicNaVysek} pozic)`);
      }
    }

    // Předsek
    const predsek = document.getElementById('predsek')?.value;
    if (predsek && predsek !== '0') {
      const predsekText = this.getPredsekText(predsek);
      const pozicNaPredsek = document.getElementById('pozicNaPredsek')?.value || '1';
      if (predsekText) {
        dodatecneUpravy.push(`Předsek: ${predsekText} (${pozicNaPredsek} pozic)`);
      }
    }

    // Ražba
    const razba = document.getElementById('razba')?.value;
    if (razba && razba !== '0') {
      const razbaText = this.getRazbaText(razba);
      const pozicNaRazbu = document.getElementById('pozicNaRazbu')?.value || '1';
      if (razbaText) {
        dodatecneUpravy.push(`Ražba: ${razbaText} (${pozicNaRazbu} pozic)`);
      }
    }

    // Vrtání
    const vrtani = document.getElementById('vrtani')?.value;
    if (vrtani && vrtani !== '0') {
      const vrtaniText = this.getVrtaniText(vrtani);
      if (vrtaniText) {
        dodatecneUpravy.push(`Vrtání: ${vrtaniText}`);
      }
    }

    // Perforace
    const perforace = document.getElementById('perforace')?.value;
    if (perforace && perforace !== '0') {
      const pocetPerforaci = parseInt(perforace);
      if (pocetPerforaci > 0) {
        dodatecneUpravy.push(`Perforace ${pocetPerforaci}x`);
      }
    }

    // Lepení
    const lepeni = document.getElementById('lepeni')?.value;
    if (lepeni && lepeni !== '0') {
      const lepeniText = this.getLepeniText(lepeni);
      if (lepeniText) {
        dodatecneUpravy.push(lepeniText);
      }
    }

    // Vazba
    const vazba = document.getElementById('vazba')?.value;
    if (vazba && vazba !== '0') {
      const vazbaText = this.getVazbaText(vazba);
      if (vazbaText) {
        dodatecneUpravy.push(vazbaText);
      }
    }

    return dodatecneUpravy;
  }

  // Pomocné funkce pro převod hodnot na texty (duplikáty z backend kódu)
  getDigitalColorName(typ) {
    const nazvy = {
      'lak_10_50': '1/0 lak (parciální)',
      'lak_11_50': '1/1 lak (parciální)',
      'lak_10_100': '1/0 lak (celá plocha)',
      'lak_11_100': '1/1 lak (celá plocha)',
      'bila_10_50': '1/0 bílá (parciální)',
      'bila_11_50': '1/1 bílá (parciální)',
      'bila_10_100': '1/0 bílá (celá plocha)',
      'bila_11_100': '1/1 bílá (celá plocha)',
      'zlata_10_50': '1/0 zlatá (parciální)',
      'zlata_11_50': '1/1 zlatá (parciální)',
      'zlata_10_100': '1/0 zlatá (celá plocha)',
      'zlata_11_100': '1/1 zlatá (celá plocha)'
    };
    return nazvy[typ] || '';
  }

  getLakText(typ) {
    const nazvy = {
      'parcial10': '1/0 UV lak parciální',
      'parcial11': '1/1 UV lak parciální',
      'plocha10': '1/0 UV lak celá plocha',
      'plocha11': '1/1 UV lak celá plocha',
      'stroj10': '1/0 Stroj lak',
      'stroj11': '1/1 Stroj lak'
    };
    return nazvy[typ] || '';
  }

  getLaminaceText(typ) {
    const nazvy = {
      'lesk10': '1/0 Lamino lesklé',
      'lesk11': '1/1 Lamino lesklé',
      'mat10': '1/0 Lamino matné',
      'mat11': '1/1 Lamino matné',
      'struktura10': '1/0 Lamino nepoškrabatelné',
      'struktura11': '1/1 Lamino nepoškrabatelné',
      'samet10': '1/0 Lamino sametové',
      'samet11': '1/1 Lamino sametové',
      'kapsaA5': '1/1 Laminace kapsová (A5)',
      'kapsaA4': '1/1 Laminace kapsová (A4)',
      'kapsaA3': '1/1 Laminace kapsová (A3)'
    };
    return nazvy[typ] || '';
  }

  getFalcovaniText(typ) {
    const nazvy = {
      'half-fold': 'Na polovinu (1 lom)',
      'cross-fold': 'Křížový lom (2 lomy)',
      'u-fold': 'Do U (2 lomy)',
      'z-fold': 'Do Z (2 lomy)',
      'double-half-fold': 'Na polovinu (2 lomy)',
      'roll-fold': 'Harmonikové s přebalem (3 lomy)',
      'harmonium-fold-3': 'Harmonikové skládání (3 lomy)',
      'harmonium-fold-4': 'Harmonikové skládání (4 lomy)',
      'harmonium-fold-5': 'Harmonikové skládání (5 lomů)',
      'gate-fold': 'Skládání do psaníčka (3 lomy)',
      'double-gate-fold': 'Zavřené psaníčko (3 lomy)'
    };
    return nazvy[typ] || '';
  }

  getBigovaniText(typ) {
    const nazvy = {
      'half-fold': 'Na polovinu (1 big)',
      'cross-fold': 'Do kříže (2 bigy)',
      'u-fold': 'Do U (2 bigy)',
      'z-fold': 'Do Z (2 bigy)',
      'double-half-fold': 'Na polovinu (2 bigy)',
      'roll-fold': 'Harmonikové (3 bigy)',
      'harmonium-fold-3': 'Harmonikové (3 bigy)',
      'harmonium-fold-4': 'Harmonikové (4 bigy)',
      'harmonium-fold-5': 'Harmonikové (5 bigy)',
      'gate-fold': 'Skládání do psaníčka (3 bigy)',
      'double-gate-fold': 'Zavřené psaníčko (3 bigy)'
    };
    return nazvy[typ] || '';
  }

  getVysekText(option) {
    const nazvy = {
      'vlastni': 'Vlastní/půjčené výsekové železo',
      'nove': 'Nové výsekové železo'
    };
    return nazvy[option] || '';
  }

  getPredsekText(option) {
    const nazvy = {
      '1': 'Vlastní/půjčené předsekové železo',
      '2': 'Nové předsekové železo'
    };
    return nazvy[option] || '';
  }

  getRazbaText(option) {
    const nazvy = {
      '1': 'Vlastní/půjčený štoček',
      '2': 'Nový štoček'
    };
    return nazvy[option] || '';
  }

  getVrtaniText(typ) {
    const nazvy = {
      '1dira2mm': '1 díra (2 mm)',
      '1dira4mm': '1 díra (4 mm)',
      '1dira5mm': '1 díra (5,5 mm)',
      '2diry2mm': '2 díry (2 mm)',
      '2diry4mm': '2 díry (4 mm)',
      '2diry5mm': '2 díry (5,5 mm)',
      'euro2diry2mm': 'Euro blok, 2 díry (2 mm)',
      'euro2diry4mm': 'Euro blok, 2 díry (4 mm)',
      'euro2diry5mm': 'Euro blok, 2 díry (5,5 mm)',
      '3diry2mm': '3 díry (2 mm)',
      '3diry4mm': '3 díry (4 mm)',
      '3diry5mm': '3 díry (5,5 mm)',
      'euro4diry2mm': 'Euro blok, 4 díry (2 mm)',
      'euro4diry4mm': 'Euro blok, 4 díry (4 mm)',
      'euro4diry5mm': 'Euro blok, 4 díry (5,5 mm)'
    };
    return nazvy[typ] || '';
  }

  getLepeniText(typ) {
    const nazvy = {
      '1': 'Lepení krabiček'
    };
    return nazvy[typ] || '';
  }

  getVazbaText(typ) {
    const nazvy = {
      'V1-strojni': 'V1 (2 skoby) strojní',
      'V1-rucni': 'V1 (2 skoby) ručně',
      'blok': 'Lepený blok',
      'blokPrebal': 'Lepený blok s přebalem',
      'V2-PUR': 'V2 lepená (PUR)',
      'V3': 'V3 lepená a šitá',
      'V6': 'V6 leporelo',
      'krouzek-blok': 'Kroužková twin wire',
      'krouzek-kalendar': 'Kroužková s háčkem na pověšení',
      'V4': 'V4 šitá, měkká obálka',
      'V5': 'V5 šitá, tvrdá obálka',
      'V2V8': 'V2V8 – falešná V8',
      'V8': 'V8 šitá, tvrdá obálka'
    };
    return nazvy[typ] || 'Neznámá vazba';
  }

  // Vykreslení seznamu položek
  renderItems() {
    const container = document.getElementById('seznamPolozek');
    if (!container) return;

    container.innerHTML = this.items.map(item => `
      <div class="flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${
        item.id === this.activeItemId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
      }" data-item-id="${item.id}">
        <div class="flex-1 min-w-0" onclick="window.offerItemsManager.switchToItem(${item.id})">
          <div class="font-medium text-sm text-gray-900">${this.escapeHtml(item.name)}</div>
          <div class="text-sm text-gray-500">
            ${item.price.toLocaleString('cs-CZ')} Kč
            ${item.pricePerPiece > 0 ? `(${item.pricePerPiece.toFixed(2)} Kč/ks)` : ''}
          </div>
        </div>
        <div class="flex flex-col items-center justify-center gap-0.5">
          <!-- Přetáhnout (drag handle) -->
          <div class="drag-handle p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing focus:outline-none" title="Přetáhnout pro změnu pořadí" aria-label="Přetáhnout pro změnu pořadí">
            ${icon('arrows-up-down', 'w-4 h-4')}
          </div>
          <!-- Duplikovat -->
          <button type="button" onclick="window.offerItemsManager.duplicateItem(${item.id})"
            class="p-1 text-gray-400 hover:text-gray-600 focus:outline-none" title="Duplikovat položku">
            ${icon('squares-2-stack', 'w-4 h-4')}
          </button>
          <!-- Smazat -->
          ${this.items.length > 1 ? `
            <button type="button" onclick="window.offerItemsManager.removeItem(${item.id})"
              class="p-1 text-gray-400 hover:text-red-600 focus:outline-none" title="Smazat položku">
              ${icon('trash', 'w-4 h-4')}
            </button>
          ` : ''}
        </div>
      </div>
    `).join('');

    // Inicializovat nebo znovu inicializovat Sortable
    this.initializeSortable();

    // Vždy zobrazit možnost vytvořit nabídku
    const wrapper = document.getElementById('celkovaCenaWrapper');
    if (wrapper) {
      wrapper.classList.remove('hidden');
    }
  }

  // Inicializace Sortable drag & drop
  initializeSortable() {
    const container = document.getElementById('seznamPolozek');
    if (!container) return;

    // Zničit existující Sortable instanci
    if (this.sortable) {
      this.sortable.destroy();
    }

    // Vytvořit novou Sortable instanci
    this.sortable = Sortable.create(container, {
      animation: 150,
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      handle: '.drag-handle',
      onEnd: (evt) => {
        // Přeuspořádat položky
        this.reorderItems(evt.oldIndex, evt.newIndex);
      }
    });
  }

  // Přeuspořádání položek po drag & drop
  reorderItems(oldIndex, newIndex) {
    if (oldIndex === newIndex) return;

    // Uložit aktuální stav formuláře
    if (this.activeItemId) {
      const currentItem = this.items.find(item => item.id === this.activeItemId);
      if (currentItem) {
        currentItem.formData = this.saveCurrentFormState();
      }
    }

    // Přesunout položku v poli
    const movedItem = this.items.splice(oldIndex, 1)[0];
    this.items.splice(newIndex, 0, movedItem);

    // Překreslit seznam (bez reinicializace Sortable)
    this.renderItemsContent();
    
    // Aktualizovat celkovou cenu
    this.updateTotalPrice();
  }

  // Vykreslení obsahu položek bez reinicializace Sortable
  renderItemsContent() {
    const container = document.getElementById('seznamPolozek');
    if (!container) return;

    container.innerHTML = this.items.map(item => `
      <div class="flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${
        item.id === this.activeItemId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
      }" data-item-id="${item.id}">
        <div class="flex-1 min-w-0" onclick="window.offerItemsManager.switchToItem(${item.id})">
          <div class="font-medium text-sm text-gray-900">${this.escapeHtml(item.name)}</div>
          <div class="text-sm text-gray-500">
            ${item.price.toLocaleString('cs-CZ')} Kč
            ${item.pricePerPiece > 0 ? `(${item.pricePerPiece.toFixed(2)} Kč/ks)` : ''}
          </div>
        </div>
        <div class="flex flex-col items-center justify-center gap-0.5">
          <!-- Přetáhnout (drag handle) -->
          <div class="drag-handle p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing focus:outline-none" title="Přetáhnout pro změnu pořadí" aria-label="Přetáhnout pro změnu pořadí">
            ${icon('arrows-up-down', 'w-4 h-4')}
          </div>
          <!-- Duplikovat -->
          <button type="button" onclick="window.offerItemsManager.duplicateItem(${item.id})"
            class="p-1 text-gray-400 hover:text-gray-600 focus:outline-none" title="Duplikovat položku">
            ${icon('squares-2-stack', 'w-4 h-4')}
          </button>
          <!-- Smazat -->
          ${this.items.length > 1 ? `
            <button type="button" onclick="window.offerItemsManager.removeItem(${item.id})"
              class="p-1 text-gray-400 hover:text-red-600 focus:outline-none" title="Smazat položku">
              ${icon('trash', 'w-4 h-4')}
            </button>
          ` : ''}
        </div>
      </div>
    `).join('');
  }

  // Aktualizace celkové ceny
  updateTotalPrice() {
    const total = this.items.reduce((sum, item) => sum + item.price, 0);
    const totalElement = document.getElementById('celkovaCenaNabidky');
    if (totalElement) {
      totalElement.textContent = `${total.toLocaleString('cs-CZ')} Kč`;
    }
  }

  // Uložení aktuálního stavu formuláře
  saveCurrentFormState() {
    const formData = {};
    const form = document.getElementById('vypocet');
    if (!form) return formData;

    // Uložit všechny inputy, selecty a textarey
    const inputs = form.querySelectorAll('input, select, textarea');
    inputs.forEach(input => {
      if (!input.id) return;
      if (input.type === 'checkbox') {
        formData[input.id] = input.checked;
      } else if (input.type === 'radio') {
        if (input.checked) {
          formData[input.name] = input.value;
        }
      } else {
        formData[input.id] = input.value;
      }
    });

    // Materiály uložit podle názvu option (ne podle value=cena) – ceny se mohou
    // duplikovat, takže obnova podle value by vybrala špatný papír.
    const matSelect = document.getElementById('material');
    if (matSelect && matSelect.selectedIndex >= 0) {
      const opt = matSelect.options[matSelect.selectedIndex];
      if (opt && opt.value) formData.materialName = opt.textContent.trim();
    }
    const vnitrniSelect = document.getElementById('vnitrniMaterial');
    if (vnitrniSelect && vnitrniSelect.selectedIndex >= 0) {
      const opt = vnitrniSelect.options[vnitrniSelect.selectedIndex];
      if (opt && opt.value) formData.vnitrniMaterialName = opt.textContent.trim();
    }

    // Uložit i typ ceny
    const typCeny = document.getElementById('typCeny');
    const nazev = document.getElementById('nazev');
    if (typCeny) formData.typCeny = typCeny.value;
    if (nazev) formData.nazev = nazev.value;

    // Motivy – dynamické řádky nemají id ve formuláři
    if (+(formData.pocetDruhu || 1) > 1 && typeof window.getMotivyRows === 'function') {
      const rows = window.getMotivyRows().filter((m) => m.pocetKusu > 0);
      if (rows.length > 1) formData.motivy = rows;
    }

    return formData;
  }

  // Obnovení stavu formuláře
  restoreFormState(formData) {
    if (!formData) return;

    const form = document.getElementById('vypocet');
    if (!form) return;

    // Potlačit "reset-na-vlastní" listenery (Formát/Materiál) během obnovy,
    // jinak by nastavení šířky/výšky/ceny přepsalo select na "vlastní".
    const prevApplying = window._applyingPreset;
    window._applyingPreset = true;
    try {
      Object.keys(formData).forEach(key => {
        // Materiály obnovíme podle názvu až po ostatních polích.
        if (key === 'materialName' || key === 'vnitrniMaterialName') return;
        const element = document.getElementById(key);
        if (!element) return;
        if (element.type === 'checkbox') {
          element.checked = formData[key];
        } else {
          element.value = formData[key];
        }
        // Trigger change aby se aktualizovala podmíněná pole (vazba, obálka,
        // vnitřní blok, sync materiálu …) a spustil se přepočet.
        element.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Obnova materiálů podle názvu (až po ostatních polích).
      if (formData.materialName && window.selectMaterialByName) {
        window.selectMaterialByName('material', formData.materialName);
      }
      if (formData.vnitrniMaterialName && window.selectMaterialByName) {
        window.selectMaterialByName('vnitrniMaterial', formData.vnitrniMaterialName);
      }

      // Obnova motivů (dynamické řádky)
      if (Array.isArray(formData.motivy) && formData.motivy.length > 1 && typeof window.renderMotivyPanel === 'function') {
        const druhuInput = document.getElementById('pocetDruhu');
        if (druhuInput) druhuInput.value = formData.motivy.length;
        window.renderMotivyPanel(formData.motivy);
      }
    } finally {
      window._applyingPreset = prevApplying;
    }

    // Spustit přepočet (vyplní Kč/arch, g/m², výsledek).
    // Vracíme promise z pocitej(), aby volající mohl přepočet počkat
    // (např. hromadný přepočet všech položek při změně typu ceny).
    if (window.pocitej) {
      return window.pocitej();
    }
  }

  // Výchozí data formuláře
  getDefaultFormData() {
    return {
      pocetKusu: '1000',
      produktRozmerFormat: 'A4',
      produktRozmerSirka: '210',
      produktRozmerVyska: '297',
      pocetBarev: '4/4',
      primeBarvy: '0',
      barvyDigital: '0',
      material: 'Krida_lesk_115',
      materialGramaz: '115',
      materialCena: '',
      materialRozmerSirka: '',
      materialRozmerVyska: '',
      lamino: '0',
      laminoOboustranne: false,
      plocha: '0',
      soutisk: false,
      bigovani: '0',
      vysek: '0',
      pozicNaVysek: '1',
      cenaRazniceVyseku: '0',
      predsek: '0',
      pozicNaPredsek: '1',
      cenaRaznicePredseku: '0',
      razba: '0',
      pozicNaRazbu: '1',
      cenaStocku: '0',
      doprava: '0',
      nazev: '',
      typCeny: 'velko'
    };
  }

  // Vytvoření nabídky
  async createOffer() {
    try {
      // Uložit aktuální stav formuláře
      if (this.activeItemId) {
        const currentItem = this.items.find(item => item.id === this.activeItemId);
        if (currentItem) {
          currentItem.formData = this.saveCurrentFormState();
        }
      }

      // Připravit data pro odeslání
      const offerData = {
        items: this.items.map(item => ({
          name: item.name,
          formData: item.formData,
          price: item.price,
          pricePerPiece: item.pricePerPiece,
          debugInfo: item.debugInfo,
          // Strukturovaný výsledek kalkulace pro technický list (výroba):
          // bez objemných textových polí, ta jsou už v debugInfo.
          calculationResult: this.stripCalculationResult(item.calculationResult)
        })),
        totalPrice: this.items.reduce((sum, item) => sum + item.price, 0),
        createdAt: new Date().toISOString(),
        customerId: this.selectedCustomer?.id || null,
        customerData: this.selectedCustomer || null
      };

      // Odeslat na backend
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
        ? 'http://localhost:1234/api' 
        : '/api';
      const response = await fetch(`${API_BASE_URL}/offers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(offerData)
      });

      if (!response.ok) {
        throw new Error('Chyba při vytváření nabídky');
      }

      const result = await response.json();
      
      // Zobrazit notifikaci
      if (window.showNotification) {
        window.showNotification('Nabídka byla úspěšně vytvořena', 'success');
      }

      // Přejít na sekci nabídek
      if (window.sectionManager) {
        window.sectionManager.showSection('nabidky');
      }

    } catch (error) {
      console.error('Chyba při vytváření nabídky:', error);
      if (window.showNotification) {
        window.showNotification('Chyba při vytváření nabídky', 'error');
      }
    }
  }

  /**
   * Ořeže výsledek kalkulace pro uložení do nabídky: vynechá objemné
   * textové výstupy (debugInfo, textOutput) – strukturovaná data
   * (comp, vazba, motivy, počty archů, technologie…) zůstávají pro
   * technický list ve výrobě.
   */
  stripCalculationResult(result) {
    if (!result || typeof result !== 'object') return null;
    const { debugInfo, textOutput, ...rest } = result;
    try {
      return JSON.parse(JSON.stringify(rest));
    } catch {
      return null;
    }
  }

  // Pomocná funkce pro escapování HTML
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Export pro použití v app.js
window.OfferItemsManager = OfferItemsManager;
